import React from 'react';
import styled from '@emotion/styled';
import isHotkey from 'is-hotkey';
import { Button, TextField, Tooltip } from '@mui/material';
import { ContactNotesHookData } from '../../data/useContactNotes';
import { TimeDisplay } from '../../../schemed/TimeDisplay';
import { DictionarySelectDisplay } from '../../../schemed/Select';
import { SmallAddButton } from '../Primitives';
import { Add } from '@mui/icons-material';
import { useFormats } from '../../../schemed';
import { MoreActionsMenuButton } from '../../../primitives';

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoteCard = styled.div`
  background: ${props => props.theme.palette.background.default};
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.palette.divider};
  box-shadow: 2px 2px 5px -4px #22222280;
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
  font-size: 0.8125rem;
  opacity: 0.75;
  color: ${props => props.theme.palette.text.secondary};
  user-select: none;

  & .MuiButtonBase-root {
    padding: 0;
    font-size: inherit;
    & .MuiSvgIcon-root {
      font-size: inherit;

    }
  }
`;

const NoteText = styled.div`
  white-space: pre-line;
  word-break: break-word;
  line-height: 1.2;
`;

const AddNoteBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

interface Props {
  data: ContactNotesHookData;
}

export const ContactNotesSection = ({ data }: Props) => {
  const { notes, schema, newNote, removeNote } = data;
  
  // Distinguish between adding a new note vs editing an existing one
  const isAddingNew = !!newNote.item && !newNote.item._id;
  const formats = useFormats();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isHotkey("mod+Enter", e)) {
      e.preventDefault();
      newNote.save();
    }
  };

  return (
    <NotesContainer>
      
      {/* Header & Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!newNote.item && (
          <SmallAddButton icon={<Add />} onClick={() => newNote.startEditing({})}>
            добавить заметку
          </SmallAddButton>
        )}
        <span />
      </div>

      {/* NEW Note Editor */}
      {isAddingNew && (
        <AddNoteBox>
          <TextField
            autoFocus
            multiline
            placeholder="Введите текст заметки... (Ctrl+Enter для сохранения)"
            value={newNote.item?.message || ""}
            onChange={e => newNote.update({ message: e.target.value })}
            onKeyDown={handleKeyDown}
            fullWidth
            size="small"
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => newNote.cancel()}>
              Отмена
            </Button>
            <Button size="small" color="primary" onClick={() => newNote.save()}>
              добавить
            </Button>
          </div>
        </AddNoteBox>
      )}

      {/* Notes List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        
        {notes.map(note => {
          const isEditingThisNote = newNote.item?._id === note._id;

          return (
            <NoteCard key={note._id}>
              <NoteHeader>
                <span>
                  <DictionarySelectDisplay 
                    schema={schema.created_by} 
                    row={note}
                    field="created_by" 
                  />
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Tooltip title={
                    <TimeDisplay 
                      schema={schema.created_at} 
                      field="created_at" 
                      row={note}
                      config={{ utcToLocal: true }}
                    />
                  }>
                    <span>
                      <TimeDisplay 
                        schema={schema.created_at} 
                        field="created_at" 
                        row={note}
                        config={{ utcToLocal: true, format: formats.date }}
                      />
                    </span>
                  </Tooltip>
                  
                  {/* Action Menu placed right after the time */}
                  {!isEditingThisNote && (
                    <MoreActionsMenuButton
                      actions={[
                        ["edit", "редактировать", () => newNote.startEditing(note)],
                        ["delete", "удалить", () => removeNote.run(note)],
                      ]}
                    />
                  )}
                </div>
              </NoteHeader>

              {/* Edit Existing Note OR Display Text */}
              {isEditingThisNote ? (
                <AddNoteBox style={{ marginTop: '8px', marginBottom: 0 }}>
                  <TextField
                    autoFocus
                    multiline
                    value={newNote.item?.message || ""}
                    onChange={e => newNote.update({ message: e.target.value })}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    size="small"
                  />
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => newNote.cancel()}>
                      Отмена
                    </Button>
                    <Button size="small" color="primary" onClick={() => newNote.save()}>
                      сохранить
                    </Button>
                  </div>
                </AddNoteBox>
              ) : (
                <NoteText>{note.message}</NoteText>
              )}
            </NoteCard>
          );
        })}
      </div>
    </NotesContainer>
  );
};